import type { SyncBackendKind } from "../ports/sync.js";

export type StorageUseCase =
  | "project-source"
  | "public-runtime"
  | "conversation-backup"
  | "large-asset-backup"
  | "self-hosted"
  | "local-backup";

export type StorageProviderStatus = "ready" | "planned" | "blocked";

export interface StorageProviderDescriptor {
  id: SyncBackendKind;
  label: string;
  status: StorageProviderStatus;
  directClient: boolean;
  recommendedFor: StorageUseCase[];
  serverPolicy: "metadata-only" | "roqeet-hosted-content";
  notes: string;
}

export const STORAGE_PROVIDER_CATALOG: StorageProviderDescriptor[] = [
  {
    id: "file",
    label: "Local .rqp export/import",
    status: "ready",
    directClient: true,
    recommendedFor: ["local-backup"],
    serverPolicy: "metadata-only",
    notes: "Offline package import/export. No network transport.",
  },
  {
    id: "github",
    label: "GitHub",
    status: "ready",
    directClient: true,
    recommendedFor: ["project-source"],
    serverPolicy: "metadata-only",
    notes: "Best for developer project source, .rqt/.rqg versioning, and assets in a repo.",
  },
  {
    id: "roqeet",
    label: "RoQeet storage",
    status: "planned",
    directClient: false,
    recommendedFor: ["public-runtime"],
    serverPolicy: "roqeet-hosted-content",
    notes: "Use for latest public runnable builds and listing metadata, not private backups.",
  },
  {
    id: "google-drive",
    label: "Google Drive",
    status: "ready",
    directClient: true,
    recommendedFor: ["conversation-backup"],
    serverPolicy: "metadata-only",
    notes: "Direct appDataFolder sync through the user's Google token.",
  },
  {
    id: "webdav",
    label: "WebDAV",
    status: "ready",
    directClient: true,
    recommendedFor: ["large-asset-backup", "self-hosted", "conversation-backup"],
    serverPolicy: "metadata-only",
    notes: "Good first-class self-hosted backend when CORS is configured.",
  },
  {
    id: "dropbox",
    label: "Dropbox",
    status: "ready",
    directClient: true,
    recommendedFor: ["conversation-backup"],
    serverPolicy: "metadata-only",
    notes: "Direct Dropbox API transport with user OAuth token.",
  },
  {
    id: "onedrive",
    label: "OneDrive",
    status: "ready",
    directClient: true,
    recommendedFor: ["conversation-backup"],
    serverPolicy: "metadata-only",
    notes: "Direct Microsoft Graph drive transport with user OAuth token.",
  },
  {
    id: "pcloud",
    label: "pCloud",
    status: "ready",
    directClient: true,
    recommendedFor: ["large-asset-backup", "conversation-backup"],
    serverPolicy: "metadata-only",
    notes: "Direct pCloud API transport with user token.",
  },
  {
    id: "s3-compatible",
    label: "S3-compatible",
    status: "ready",
    directClient: true,
    recommendedFor: ["large-asset-backup", "self-hosted"],
    serverPolicy: "metadata-only",
    notes: "Direct S3-compatible object storage. Browser CORS and scoped credentials are required.",
  },
  {
    id: "mega",
    label: "MEGA",
    status: "blocked",
    directClient: true,
    recommendedFor: ["large-asset-backup", "conversation-backup"],
    serverPolicy: "metadata-only",
    notes: "Requires a browser-capable MEGA SDK/auth implementation. Do not proxy through RoQeet servers.",
  },
  {
    id: "proton-drive",
    label: "Proton Drive",
    status: "blocked",
    directClient: true,
    recommendedFor: ["large-asset-backup", "conversation-backup"],
    serverPolicy: "metadata-only",
    notes: "Requires Proton-compatible client crypto/API support. Do not proxy through RoQeet servers.",
  },
  {
    id: "rclone",
    label: "rclone config import",
    status: "ready",
    directClient: true,
    recommendedFor: ["self-hosted", "large-asset-backup"],
    serverPolicy: "metadata-only",
    notes: "Imports compatible remote profiles. Browser use still depends on matching direct provider APIs.",
  },
];

export function getStorageProviderDescriptor(id: string): StorageProviderDescriptor | undefined {
  return STORAGE_PROVIDER_CATALOG.find((provider) => provider.id === id);
}

export interface RcloneRemote {
  name: string;
  type: string;
  values: Record<string, string>;
  providerId?: SyncBackendKind;
}

export function parseRcloneConfig(text: string): RcloneRemote[] {
  const remotes: RcloneRemote[] = [];
  let current: RcloneRemote | undefined;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#") || line.startsWith(";")) continue;

    const section = /^\[([^\]]+)\]$/.exec(line);
    if (section) {
      if (current) remotes.push(withProviderId(current));
      current = { name: section[1]!.trim(), type: "", values: {} };
      continue;
    }

    const eq = /^([^=]+?)\s*=\s*(.*)$/.exec(line);
    if (!eq || !current) continue;
    const key = eq[1]!.trim();
    const value = eq[2]!.trim();
    current.values[key] = value;
    if (key === "type") current.type = value;
  }

  if (current) remotes.push(withProviderId(current));
  return remotes;
}

function withProviderId(remote: RcloneRemote): RcloneRemote {
  const providerId = providerIdFromRcloneType(remote.type);
  return providerId ? { ...remote, providerId } : remote;
}

export function providerIdFromRcloneType(type: string): SyncBackendKind | undefined {
  switch (type.toLowerCase()) {
    case "drive":
    case "google cloud storage":
      return "google-drive";
    case "dropbox":
      return "dropbox";
    case "onedrive":
      return "onedrive";
    case "pcloud":
      return "pcloud";
    case "s3":
      return "s3-compatible";
    case "webdav":
      return "webdav";
    case "mega":
      return "mega";
    case "protondrive":
    case "proton drive":
      return "proton-drive";
    default:
      return undefined;
  }
}
